import React, { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useApiFormAppDetails, useApiFormAppDownload } from '../../api-new/state/config'
import { APP_STORES, appStoreHref } from '../../shared/app-stores/app-stores'
import { dateText } from '../../shared/date/local-time'
import { ErrorReference } from '../../shared/error/error-reference'
import { AuthLoading, AuthNotice } from '../../shared/sso-workspace/auth-card'
import { IconDownload, IconNext } from '../../shared/icon/icon'
import '../../shared/sso-workspace/sso-workspace.scss'
import '../../shared/sso-workspace/auth-card.scss'
import './download-app.scss'

// /download/app/:appStoreKey -- one store's build of the EducationXR app
// (config's app-download): the app with its Download button, what's new in
// the version, the other stores, and about EducationXR. One layout: two
// columns from lg up, one below (download-app.scss). The site frame around
// it supplies the header, logo and footer.

// "Oct 8, 2026" in the viewer's zone; '' for no date (Go's zero time too).
export const releaseText = value => {
  const ms = Date.parse(value)
  return Number.isFinite(ms) && ms > 0 ? dateText(ms) : ''
}

const present = value => value !== undefined && value !== null && value !== ''

// The Download button's address, always absolute (prod 2026-10-22): config's
// storageDownloadURL can come without a scheme ("s3-us-west-2.amazonaws.com/
// educationxr-build/<key>"), and a browser takes that as a path on this site
// (/download/app/s3-us-west-2.amazonaws.com/…: "We couldn't find this app").
// A schemeless or protocol-relative address is the file over https.
export const downloadHref = url => {
  const value = String(url || '').trim()
  if (!value) return ''
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return value
  if (value.startsWith('//')) return `https:${value}`
  return `https://${value}`
}

// "Version 1.5.0 · Released Oct 8, 2026 · Build 42", leaving out what's empty.
export const metaText = form => {
  const released = releaseText(form.releaseDate)
  return [
    present(form.versionName) && `Version ${form.versionName}`,
    released && `Released ${released}`,
    present(form.versionCode) && `Build ${form.versionCode}`
  ].filter(Boolean).join(' · ')
}

export const DownloadApp = () => {
  const { appStoreKey } = useParams()
  const apiFormAppDownload = useApiFormAppDownload()
  const apiRef = useRef(apiFormAppDownload)
  apiRef.current = apiFormAppDownload

  // loading -> ready | failed (failure: what the api layer threw)
  const [load, setLoad] = useState({ step: 'loading', failure: null })

  useEffect(() => {
    if (!appStoreKey) return
    let current = true
    setLoad({ step: 'loading', failure: null })
    apiRef.current
      .get(`${appStoreKey}/store`)
      .then(() => current && setLoad({ step: 'ready', failure: null }))
      .catch(failure => current && setLoad({ step: 'failed', failure: failure || null }))
    return () => {
      current = false
    }
  }, [appStoreKey])

  const form = apiFormAppDownload.form || {}
  const others = APP_STORES.filter(store => store.key !== appStoreKey)

  return (
    <div className="h-100 overflow-auto">
      <div className="sw-workspace dl-app">
        {load.step === 'loading' &&
          <section className="sw-section dl-card-area">
            <AuthLoading>Loading the app…</AuthLoading>
          </section>}

        {load.step === 'failed' &&
          <section className="sw-section dl-card-area">
            <AuthNotice tone="bad" role="alert">
              <p>
                {load.failure && load.failure.status === 404
                  ? "We couldn't find this app. Choose your platform from the list."
                  : "We couldn't load this app. Please try again in a moment."}
              </p>
            </AuthNotice>
            <ErrorReference error={load.failure} className="mt-3" />
          </section>}

        {load.step === 'ready' &&
          <React.Fragment>
            <AppCard form={form} />
            {form.versionNotesHTML && <AppNotes versionName={form.versionName} value={form.versionNotesHTML} />}
          </React.Fragment>}

        {others.length > 0 && <OtherPlatforms stores={others} />}

        <AppDescription />
      </div>
    </div>
  )
}

const AppCard = ({ form }) => {
  const meta = metaText(form)
  return (
    <section className="sw-section dl-card-area dl-card" aria-labelledby="dl-title">
      <img className="dl-icon" src="/EDXR-Icon.png" alt="" />
      <div className="dl-head">
        <h1 id="dl-title" className="dl-title">{form.storeName}</h1>
        {meta && <p className="dl-meta">{meta}</p>}
      </div>
      {form.storageDownloadURL &&
        <a className="btn sw-btn-primary dl-download" href={downloadHref(form.storageDownloadURL)} target="_blank" rel="noreferrer">
          <IconDownload />
          Download
        </a>}
    </section>
  )
}

const AppNotes = ({ versionName, value }) => {
  function createMarkup() {
    return { __html: value }
  }

  return (
    <section className="sw-section dl-notes-area" aria-labelledby="dl-notes-title">
      <h2 id="dl-notes-title">What’s new{present(versionName) ? ` in ${versionName}` : ''}</h2>
      <div className="dl-notes" dangerouslySetInnerHTML={createMarkup()} />
    </section>
  )
}

// "Version 1.5.0 · Updated Oct 8, 2026" from config's app-details; '' when
// there is nothing to say.
export const platformText = details => {
  if (!details) return ''
  const updated = releaseText(details.releaseDate)
  return [present(details.versionName) && `Version ${details.versionName}`, updated && `Updated ${updated}`].filter(Boolean).join(' · ')
}

// Each listed store's latest version, all asked at once on mount. A row
// whose call fails or hasn't answered yet is just its name: the list never
// waits on, or errors over, another store.
const useStoreDetails = stores => {
  const apiFormAppDetails = useApiFormAppDetails()
  const apiRef = useRef(apiFormAppDetails)
  apiRef.current = apiFormAppDetails
  const keys = stores.map(store => store.key).join(',')
  const [details, setDetails] = useState({})

  useEffect(() => {
    let current = true
    setDetails({})
    keys.split(',').filter(Boolean).forEach(key => {
      apiRef.current
        .get(`${key}/store`)
        .then(data => current && data && setDetails(prev => ({ ...prev, [key]: data })))
        .catch(() => {})
    })
    return () => {
      current = false
    }
  }, [keys])

  return details
}

const OtherPlatforms = ({ stores }) => {
  const details = useStoreDetails(stores)
  return (
    <section className="sw-section dl-other-area" aria-labelledby="dl-other-title">
      <h2 id="dl-other-title">Other platforms</h2>
      <ul className="dl-platforms">
        {stores.map(store => {
          const sub = platformText(details[store.key])
          return (
            <li key={store.key}>
              <a className="dl-platform" href={appStoreHref(store.key)} target="_blank" rel="noreferrer">
                <span className="dl-platform-text">
                  <span className="dl-platform-name">{store.name}</span>
                  {sub && <span className="dl-platform-sub">{sub}</span>}
                </span>
                <IconNext className="dl-platform-chevron" />
              </a>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

const AppDescription = () => {
  return (
    <section className="dl-about-area dl-about" aria-labelledby="dl-about-title">
      <h2 id="dl-about-title" className="dl-eyebrow">About EducationXR</h2>
      <p>
        EducationXR is an extended reality (XR) platform used to deliver immersive education content to mobile, tablet, desktop and virtual reality
        applications. This application provides users with the ability to connect together with other users in one singular experience regardless of location
        and hardware.
      </p>

      <p>
        EducationXR has been custom-built from the ground up to deliver quality, real-time 3D education trusted by superior institution and industry names
        including, GE, UNMC, Bracco, and Harvard’s MGH.
      </p>

      <p>
        Contact Us: Any feedback? Email us at{' '}
        <a href="mailto:info@educationxr.com">
          info@educationxr.com
        </a>
      </p>
    </section>
  )
}
